import { useEffect, useMemo, useRef, useState } from "react";
import { resolveFieldOptions } from "./advance-filter.helpers";

// Options for every list field of the advance filter. Like EntityFormModal,
// the page's `dependenciesLoader` fetches every dropdown's data in one call
// (each page decides which APIs that is). Nothing is fetched until `enabled`
// turns true (the drawer opens), so a page that never opens the filter makes
// no extra API calls. Results are reused on later opens until `reloadKey`
// changes (the page's Refresh).
const useAdvanceFilterOptions = (fields, dependenciesLoader, enabled, reloadKey) => {
  const [dependencies, setDependencies] = useState({});
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    fetchedRef.current = false;
  }, [reloadKey]);

  useEffect(() => {
    if (!enabled || fetchedRef.current || !dependenciesLoader) return;

    fetchedRef.current = true;
    let active = true;
    let done = false;
    setLoading(true);

    Promise.resolve()
      .then(dependenciesLoader)
      .catch(() => ({}))
      .then((result) => {
        done = true;
        if (!active) return;
        setDependencies(result || {});
        setLoading(false);
      });

    return () => {
      active = false;
      // Closed before the options arrived: let the next open fetch again.
      if (!done) {
        fetchedRef.current = false;
        setLoading(false);
      }
    };
    // The loader is a module-level constant; only `enabled` should trigger the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const optionsByKey = useMemo(() => {
    const result = {};
    for (const field of fields) result[field.key] = resolveFieldOptions(field, dependencies);
    return result;
  }, [fields, dependencies]);

  return { optionsByKey, loading };
};

export default useAdvanceFilterOptions;
